import { CategoryName, TDatabaseId } from '@infotech-soe/metaschema';

import CachedSchemaRepository from '../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../infrastructure/schemas/storage/ISchemaRepository';
import { CategoryTreeStateEntity } from '../../models/category/categoryTree';
import CategoryViewModel from '../category/view-model/CategoryViewModel';
import { DatasetCurrentState } from '../dataset/view-model/DatasetCurrentState';
import { DatasetGridRow } from '../dataset/view-model/DatasetGridRow';
import UniqueId from '../uid/UniqueId';

import IContextService, { ClientContext } from './IContextService';

class ContextServiceSingleton implements IContextService {
  private readonly schemaRepository: ISchemaRepository;
  constructor() {
    this.schemaRepository = new CachedSchemaRepository();
  }

  public async getContext(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId,
    treeState?: CategoryTreeStateEntity
  ): Promise<ClientContext> {
    const dataset = category.datasets[datasetId];
    const datasetState = datasetsState[datasetId];

    const displayMode = await this.schemaRepository.getDisplayMode(
      category.source.category,
      category.source.displayMode
    );

    const datasetSource = displayMode.datasets[dataset.name].source;

    const context = new ClientContext();

    const categorySource = await this.getCategoryBySource(datasetSource);

    context.set('Category', categorySource);
    context.set('MasterCategory', category.source.category);
    context.set('DisplayMode', category.source.displayMode);
    context.set('InParams', category.source.inParams);

    context.set('ProcessId', category.processId);

    if (datasetState && datasetState.activeRowId !== null) {
      context.set('Id', Number(datasetState.activeRowId));
      if (categorySource === category.source.category) {
        context.set('Master', Number(datasetState.activeRowId));
      }
    }

    if (datasetState && datasetState.selectedIds.length > 0) {
      context.set('SelectedIds', datasetState.selectedIds);
    }

    if (datasetState && datasetState.catalogContext !== null) {
      const activeCatalog = datasetState.catalogContext.activeId;
      if (activeCatalog !== null) {
        context.set('Catalog', Number(activeCatalog));
        context.set(
          'TreeNode',
          treeState?.nodes.filter(i => i.id === activeCatalog)[0]
        );
      }
    }

    if (dataset.parent) {
      const parentContext = await this.getContext(
        category,
        datasetsState,
        dataset.parent
      );

      if (parentContext.has('Master')) {
        context.set('Master', parentContext.get('Master'));
      }

      if (parentContext.has('Category')) {
        context.set('ParentCategory', parentContext.get('Category'));
      }

      if (parentContext.has('Id')) {
        context.set('Parent', parentContext.get('Id'));
      }

      if (parentContext.has('Catalog')) {
        context.set('Catalog', parentContext.get('Catalog'));
      }
    }

    const row = this.getDatasetRow(
      datasetsState,
      context.get('Category'),
      context.get('Id'),
      datasetId
    );
    if (row !== undefined) {
      context.set('Row', row);
    }

    const parentRow = this.getDatasetRow(
      datasetsState,
      context.get('ParentCategory'),
      context.get('Parent')
    );
    if (parentRow !== undefined) {
      context.set('ParentRow', parentRow);
    }

    const masterRow = this.getDatasetRow(
      datasetsState,
      context.get('MasterCategory'),
      context.get('Master')
    );
    if (masterRow !== undefined) {
      context.set('MasterRow', masterRow);
    }

    return context;
  }

  private getDatasetRow(
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    category: CategoryName,
    rowId?: TDatabaseId,
    datasetId?: UniqueId
  ): DatasetGridRow | undefined {
    if (rowId === undefined) {
      return undefined;
    }

    const datasets = Object.entries(datasetsState);
    if (datasetId !== undefined) {
      datasets.sort(([id]) => (id === datasetId ? -1 : 1));
    }

    for (const [, dataset] of datasets) {
      if (dataset.source.category === category) {
        for (const row of dataset.rows) {
          if (row[dataset.rowIdField] === rowId) {
            return row;
          }
        }
      }
    }
  }

  private async getCategoryBySource(source: string): Promise<CategoryName> {
    try {
      const view = await this.schemaRepository.getView(source);
      return view.Base;
    } catch {
      await this.schemaRepository.getCategory(source);
      return source;
    }
  }
}

const ContextService = (() => {
  let instance: IContextService;
  return new Proxy(ContextServiceSingleton, {
    construct(Target: typeof ContextServiceSingleton): IContextService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default ContextService;
